import React from 'react';
import { Link } from 'react-router-dom';
import useDocumentTitle from '../../hooks/useDocumentTitle';

const PrivacyPolicyPage: React.FC = () => {
  useDocumentTitle('Privacy Policy');

  return (
    <div style={{ minHeight: '100vh', background: '#f7fafc' }}>
      {/* Header */}
      <div className="border-bottom bg-white">
        <div className="container py-3">
          <div className="d-flex align-items-center justify-content-between">
            <Link to="/" className="text-decoration-none d-flex align-items-center">
              <div style={{
                width: 40, height: 40,
                background: 'linear-gradient(135deg, #06b6d4, #3b82f6)',
                borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>
                <span style={{ color: '#fff', fontWeight: 700, fontSize: '1.2rem' }}>C</span>
              </div>
              <span className="ms-2 fw-bold" style={{
                fontSize: '1.2rem',
                background: 'linear-gradient(135deg, #06b6d4, #3b82f6)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}>CloudAyn</span>
            </Link>
            <Link to="/" className="btn btn-outline-primary btn-sm">Back to Home</Link>
          </div>
        </div>
      </div>

      <div className="container py-5" style={{ maxWidth: '800px' }}>
        <h1 className="fw-bold mb-2">Privacy Policy</h1>
        <p className="text-muted mb-4">Last updated: 27 February 2026</p>

        <div className="card border-0 shadow-sm p-4 p-md-5" style={{ lineHeight: '1.7' }}>
          <section className="mb-4">
            <h4 className="fw-bold mb-3">1. Introduction</h4>
            <p>
              <strong>INFIYO TECHNOLOGIES WLL</strong> ("Company", "we", "us", or "our"), operating under the brand name
              <strong> CloudAyn</strong>, is committed to protecting your privacy and safeguarding your personal data.
              This Privacy Policy explains how we collect, use, store, disclose, and protect your information when you
              access and use the CloudAyn platform, website (<a href="https://cloudayn.com" target="_blank" rel="noopener noreferrer">cloudayn.com</a>),
              and any related services.
            </p>
            <p>
              By creating an account or using CloudAyn, you acknowledge that you have read, understood, and consent to the
              data practices described in this Privacy Policy. If you do not agree, please discontinue use of the platform.
            </p>
            <p>
              This policy applies to all users of the CloudAyn platform, including account holders, team members, and
              administrators. It does not apply to the privacy practices of websites or services operated by our users
              that install the CloudAyn analytics tracking code; those practices are governed by the respective website
              operators' own privacy policies.
            </p>
          </section>

          <section className="mb-4">
            <h4 className="fw-bold mb-3">2. Information We Collect</h4>

            <h6 className="fw-bold">2.1 Account Information</h6>
            <p>When you register for a CloudAyn account, we collect:</p>
            <ul>
              <li>Full name</li>
              <li>Email address</li>
              <li>Phone number and country code (optional)</li>
              <li>Organization name, type, and industry</li>
              <li>Country of residence or operation</li>
            </ul>

            <h6 className="fw-bold mt-3">2.2 Authentication Data</h6>
            <p>
              Depending on the authentication method you use, we may collect:
            </p>
            <ul>
              <li><strong>Email OTP:</strong> Your email address for sending one-time verification codes. We do not store passwords as our platform uses passwordless authentication.</li>
              <li><strong>Google Sign-In:</strong> When you authenticate via Google, we receive your name and email address from Google. We do not receive or store your Google password.</li>
              <li><strong>Microsoft Sign-In:</strong> When you authenticate via Microsoft, we receive your display name and email address from Microsoft. We do not receive or store your Microsoft password.</li>
            </ul>

            <h6 className="fw-bold mt-3">2.3 Payment Information</h6>
            <p>
              When you subscribe to a paid plan, payment processing is handled entirely by third-party payment gateways
              (Tap Payments and PayPal). <strong>We do not store your full credit card numbers, CVV codes, or banking credentials
              on our servers.</strong> We retain only tokenized payment references, transaction IDs, payment status, and billing
              records necessary for subscription management and tax compliance.
            </p>

            <h6 className="fw-bold mt-3">2.4 Website Analytics Data</h6>
            <p>
              Through the CloudAyn tracking code installed on your websites, we collect the following data about your
              website visitors on your behalf:
            </p>
            <ul>
              <li>Page views, navigation paths, and session duration</li>
              <li>Browser type, version, and language</li>
              <li>Device type, operating system, and screen resolution</li>
              <li>Referring URLs and traffic sources</li>
              <li>Approximate geographic location (country and city level derived from IP address)</li>
              <li>Custom events configured by you (e.g., form submissions, button clicks)</li>
            </ul>
            <p>
              This data is collected on your behalf to provide analytics insights. <strong>We do not use your website visitors'
              data for our own advertising or marketing purposes.</strong> You are the data controller for analytics data
              collected through your websites.
            </p>

            <h6 className="fw-bold mt-3">2.5 Monitoring and Security Scan Data</h6>
            <p>
              Our monitoring and security services collect technical data about your digital assets, including:
            </p>
            <ul>
              <li>Website uptime status and HTTP response times</li>
              <li>SSL/TLS certificate details and expiration dates</li>
              <li>Security headers configuration</li>
              <li>Open ports and network information</li>
              <li>Vulnerability scan results and security issues detected</li>
              <li>Server health metrics (CPU, memory, disk usage, network traffic) when you install our server agent</li>
              <li>DNS records (SPF, DKIM, DMARC) for email security monitoring</li>
            </ul>

            <h6 className="fw-bold mt-3">2.6 Platform Usage Data</h6>
            <p>
              We automatically collect information about how you interact with the CloudAyn dashboard, including
              pages visited, features used, actions taken, and general usage patterns. This data helps us improve
              the platform and user experience.
            </p>

            <h6 className="fw-bold mt-3">2.7 Technical Data</h6>
            <p>
              When you access the platform, we automatically collect technical data such as your IP address, browser
              type, device type, operating system, and access timestamps. This is used for security, fraud prevention,
              and service optimization.
            </p>
          </section>

          <section className="mb-4">
            <h4 className="fw-bold mb-3">3. How We Use Your Information</h4>
            <p>We use the information we collect for the following purposes:</p>
            <ul>
              <li><strong>Service Delivery:</strong> To provide, operate, maintain, and improve the CloudAyn platform and its features</li>
              <li><strong>Account Management:</strong> To create and manage your account, authenticate your identity, and process subscription changes</li>
              <li><strong>Payment Processing:</strong> To process subscription payments, issue receipts, and manage billing</li>
              <li><strong>Communication:</strong> To send transactional emails including OTP verification codes, payment confirmations, alert notifications, downtime alerts, and service announcements</li>
              <li><strong>Customer Support:</strong> To respond to your inquiries, troubleshoot issues, and provide technical assistance</li>
              <li><strong>Service Improvement:</strong> To analyze usage patterns, develop new features, and enhance existing functionality</li>
              <li><strong>Security:</strong> To detect and prevent fraud, unauthorized access, and other security threats to the platform</li>
              <li><strong>Legal Compliance:</strong> To comply with applicable laws, regulations, legal processes, and governmental requests</li>
              <li><strong>Analytics:</strong> To generate aggregated, anonymized statistics about platform usage for internal business purposes</li>
            </ul>
            <p>
              We will not use your personal information for purposes materially different from those described above
              without providing you with advance notice and, where required, obtaining your consent.
            </p>
          </section>

          <section className="mb-4">
            <h4 className="fw-bold mb-3">4. Data Sharing and Disclosure</h4>
            <p>
              <strong>We do not sell, rent, or trade your personal information to third parties for marketing or
              advertising purposes.</strong> We may share your information only in the following circumstances:
            </p>
            <ul>
              <li><strong>Payment Processors:</strong> Tap Payments and PayPal process subscription payments on our behalf. They receive only the information necessary to process transactions.</li>
              <li><strong>Authentication Providers:</strong> Google and Microsoft facilitate sign-in when you choose to use these methods. Data exchange is limited to basic profile information (name and email).</li>
              <li><strong>Cloud Infrastructure Providers:</strong> We use cloud hosting services (including AWS) to operate and store data. These providers are contractually bound to protect your data.</li>
              <li><strong>Email Service Providers:</strong> We use AWS Simple Email Service (SES) to send transactional emails such as OTP codes, alerts, and notifications.</li>
              <li><strong>Legal Requirements:</strong> We may disclose your information if required to do so by law, court order, subpoena, or governmental regulation, or if we believe in good faith that disclosure is necessary to protect our rights, your safety, or the safety of others.</li>
              <li><strong>Business Transfers:</strong> In the event of a merger, acquisition, or sale of all or substantially all of our assets, your data may be transferred as part of the transaction. We will provide notice before your data becomes subject to a different privacy policy.</li>
            </ul>
            <p>
              All third-party service providers are selected based on their data protection practices and are
              contractually obligated to use your data only for the specified purposes and to implement appropriate
              security measures.
            </p>
          </section>

          <section className="mb-4">
            <h4 className="fw-bold mb-3">5. Data Retention</h4>
            <p>We retain your data according to the following guidelines:</p>
            <ul>
              <li><strong>Account Data:</strong> Retained for as long as your account is active. Upon account deletion, personal data is removed within 30 days.</li>
              <li><strong>Analytics Data:</strong> Retained according to your subscription plan's data retention limits (e.g., 30 days for Free, 90 days for Starter, etc.).</li>
              <li><strong>Payment Records:</strong> Retained for a minimum of 5 years as required by tax and financial regulations in the Kingdom of Bahrain.</li>
              <li><strong>Security Scan Results:</strong> Retained for as long as your account is active. Historical scan data helps provide trend analysis.</li>
              <li><strong>Server Monitoring Data:</strong> Retained based on your subscription plan's retention period.</li>
              <li><strong>Logs and Technical Data:</strong> System logs are retained for up to 90 days for security and debugging purposes.</li>
            </ul>
            <p>
              When data is no longer needed for the purposes for which it was collected or is no longer subject to a
              legal retention requirement, it will be securely deleted or anonymized.
            </p>
          </section>

          <section className="mb-4">
            <h4 className="fw-bold mb-3">6. Data Security</h4>
            <p>
              We implement appropriate technical and organizational measures to protect your data against unauthorized
              access, alteration, disclosure, or destruction. Our security measures include:
            </p>
            <ul>
              <li><strong>Encryption in Transit:</strong> All data transmitted between your browser and our servers is encrypted using TLS/SSL (HTTPS)</li>
              <li><strong>Passwordless Authentication:</strong> Our OTP-based login eliminates the risk of password theft, brute-force attacks, and credential stuffing</li>
              <li><strong>Access Controls:</strong> Role-based access control (RBAC) ensures that users can only access data and features appropriate to their role within an organization</li>
              <li><strong>Infrastructure Security:</strong> Our cloud infrastructure is hosted on enterprise-grade platforms with physical security, network firewalls, and DDoS protection</li>
              <li><strong>Regular Security Assessments:</strong> We conduct regular security reviews and vulnerability assessments of our own platform</li>
              <li><strong>Rate Limiting:</strong> API and authentication endpoints are protected by rate limiting to prevent abuse</li>
              <li><strong>Data Isolation:</strong> Each organization's data is logically isolated to prevent unauthorized cross-access</li>
            </ul>
            <p>
              While we strive to protect your information using commercially reasonable measures, no method of electronic
              transmission or storage is 100% secure. We cannot guarantee absolute security but will promptly notify
              affected users in the event of a data breach in accordance with applicable laws.
            </p>
          </section>

          <section className="mb-4">
            <h4 className="fw-bold mb-3">7. Cookies and Tracking Technologies</h4>
            <p>
              <strong>CloudAyn Dashboard:</strong> We use essential cookies and local storage for authentication,
              session management, and remembering your preferences (such as selected organization and asset).
              These are strictly necessary for the platform to function and cannot be disabled.
            </p>
            <p>
              <strong>CloudAyn Analytics Tracking Code:</strong> The tracking code installed on your websites may use
              first-party cookies or browser fingerprinting techniques to identify unique visitors and track sessions.
              No personally identifiable information is stored in these cookies. As a website operator using CloudAyn,
              you are responsible for disclosing the use of analytics cookies in your own privacy policy and obtaining
              visitor consent where required by law (e.g., EU Cookie Directive, GDPR).
            </p>
            <p>
              <strong>We do not use:</strong> Third-party advertising cookies, cross-site tracking pixels, or any
              tracking technologies that follow users across different websites for advertising purposes.
            </p>
          </section>

          <section className="mb-4">
            <h4 className="fw-bold mb-3">8. Your Rights</h4>
            <p>
              Depending on your jurisdiction, you may have the following rights regarding your personal data:
            </p>
            <ul>
              <li><strong>Right of Access:</strong> Request a copy of the personal data we hold about you</li>
              <li><strong>Right to Rectification:</strong> Request correction of inaccurate or incomplete personal data</li>
              <li><strong>Right to Erasure:</strong> Request deletion of your personal data (subject to legal retention requirements)</li>
              <li><strong>Right to Restriction:</strong> Request restriction of processing of your data in certain circumstances</li>
              <li><strong>Right to Object:</strong> Object to processing of your personal data for specific purposes</li>
              <li><strong>Right to Data Portability:</strong> Request a machine-readable copy of your data for transfer to another service</li>
              <li><strong>Right to Withdraw Consent:</strong> Where processing is based on consent, you may withdraw it at any time without affecting the lawfulness of prior processing</li>
            </ul>
            <p>
              To exercise any of these rights, please contact us at{' '}
              <a href="mailto:info@cloudayn.com">info@cloudayn.com</a>. We will respond to your request within
              30 days. We may ask you to verify your identity before processing your request.
            </p>
            <p>
              If you believe your data protection rights have been violated, you have the right to lodge a complaint
              with the relevant data protection authority in your jurisdiction.
            </p>
          </section>

          <section className="mb-4">
            <h4 className="fw-bold mb-3">9. International Data Transfers</h4>
            <p>
              CloudAyn is operated by INFIYO TECHNOLOGIES WLL from the Kingdom of Bahrain. Your data may be processed
              and stored on servers located outside your country of residence, including in regions where our cloud
              infrastructure providers operate data centers.
            </p>
            <p>
              When we transfer data internationally, we ensure that appropriate safeguards are in place to protect
              your data in accordance with applicable data protection laws. These safeguards may include standard
              contractual clauses, data processing agreements with our service providers, and ensuring that recipients
              maintain adequate levels of data protection.
            </p>
          </section>

          <section className="mb-4">
            <h4 className="fw-bold mb-3">10. Children's Privacy</h4>
            <p>
              CloudAyn is a business-to-business platform not intended for use by individuals under the age of 18.
              We do not knowingly collect personal information from children. If we become aware that we have
              inadvertently collected data from a child under 18, we will take prompt steps to delete such information
              from our systems. If you believe a child has provided us with personal information, please contact us
              immediately at <a href="mailto:info@cloudayn.com">info@cloudayn.com</a>.
            </p>
          </section>

          <section className="mb-4">
            <h4 className="fw-bold mb-3">11. Third-Party Links</h4>
            <p>
              The CloudAyn platform may contain links to third-party websites or services. We are not responsible
              for the privacy practices of these external sites. We encourage you to review the privacy policies
              of any third-party services before providing them with your personal information.
            </p>
          </section>

          <section className="mb-4">
            <h4 className="fw-bold mb-3">12. Changes to This Privacy Policy</h4>
            <p>
              We may update this Privacy Policy from time to time to reflect changes in our practices, technology,
              legal requirements, or other factors. If we make material changes, we will notify you by:
            </p>
            <ul>
              <li>Sending an email to the address associated with your account</li>
              <li>Posting a prominent notice on the CloudAyn platform</li>
              <li>Updating the "Last updated" date at the top of this page</li>
            </ul>
            <p>
              We encourage you to review this Privacy Policy periodically. Your continued use of the platform after
              the effective date of any changes constitutes your acceptance of the updated policy.
            </p>
          </section>

          <section>
            <h4 className="fw-bold mb-3">13. Contact Us</h4>
            <p>
              If you have any questions, concerns, or requests regarding this Privacy Policy or how we handle your
              personal data, please contact us:
            </p>
            <ul className="list-unstyled">
              <li><strong>Company:</strong> INFIYO TECHNOLOGIES WLL</li>
              <li><strong>Brand:</strong> CloudAyn</li>
              <li><strong>Email:</strong> <a href="mailto:info@cloudayn.com">info@cloudayn.com</a></li>
              <li><strong>Website:</strong> <a href="https://cloudayn.com" target="_blank" rel="noopener noreferrer">https://cloudayn.com</a></li>
              <li><strong>Location:</strong> Kingdom of Bahrain</li>
            </ul>
          </section>
        </div>

        {/* Footer links */}
        <div className="text-center mt-4">
          <Link to="/terms" className="text-muted me-3">Terms & Conditions</Link>
          <Link to="/refund-policy" className="text-muted">Refund Policy</Link>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
